/**
 * /sources — "Connected Granola accounts" (plan §7.4). One compact card per
 * account: label, masked key, status, last synced, live seed progress, and a
 * View link.
 *
 * Everything you DO to an account — the meeting-notes list with its per-meeting
 * ingestion toggles, Disconnect, and Delete all Granola data — lives on that
 * View page (/sources/granola/:id), matching Slack/Signal/WhatsApp. This card
 * is an index entry: enough to see whether the connection is healthy and how
 * far indexing has got, and a way in. The meetings list in particular does not
 * belong here — a personal key can see hundreds of notes (measured: 938), so it
 * needs a page, not an accordion inside a list.
 *
 * `resync` stays at the SECTION level because the API has no :id — it resyncs
 * every account the caller owns.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Check, Loader2, NotebookPen, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { StatusPill, type StatusPillTone } from '@/components/patterns/status-pill';
import { formatRelative } from '@/components/sources/message-log';
import { Button, buttonVariants } from '@/components/ui/button';
import { type GranolaAccount, type GranolaAccountStatus, granolaAccountApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

const STATUS: Record<GranolaAccountStatus, { label: string; tone: StatusPillTone }> = {
  connecting: { label: 'Connecting', tone: 'neutral' },
  active: { label: 'Connected', tone: 'active' },
  invalid_key: { label: 'Invalid key', tone: 'danger' },
  rate_limited: { label: 'Rate limited', tone: 'paused' },
  error: { label: 'Error', tone: 'danger' },
  disconnected: { label: 'Disconnected', tone: 'paused' },
};

// ---------------------------------------------------------------------------
// Seed progress — the wizard-time count becomes an ongoing indexing status
// here, driven by the same seed_state the job persists.
// ---------------------------------------------------------------------------

function SeedProgress({ seedState }: { seedState: GranolaAccount['seedState'] }) {
  if (!seedState) return null;

  const primary =
    seedState.status === 'running' ? (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        Indexing meeting notes… {seedState.fetched.toLocaleString()} so far
      </div>
    ) : seedState.status === 'done' ? (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Check className="h-3.5 w-3.5 shrink-0 text-success" />
        Indexed {seedState.fetched.toLocaleString()} meeting{seedState.fetched === 1 ? '' : 's'}
      </div>
    ) : (
      <div className="flex items-center gap-2 text-[13px] text-destructive">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        Indexing failed — use Resync all to try again.
      </div>
    );

  return (
    <div className="mt-3 space-y-1.5">
      {primary}
      {/* R14: only a POSITIVE count is worth a line. 0 means the window
          caught everything (nothing to say); null/undefined is a genuine
          UNKNOWN (the seed was never truncated by the window, or
          visibleNoteCount itself was never captured) and must never be
          rendered as a misleading "0 notes dropped". */}
      {seedState.droppedByWindow != null && seedState.droppedByWindow > 0 && (
        <div className="flex items-center gap-2 text-[13px] text-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {seedState.droppedByWindow.toLocaleString()}{' '}
          {seedState.droppedByWindow === 1 ? 'note is' : 'notes are'} older than the seed window and
          weren&apos;t indexed. Raise GRANOLA_SEED_WINDOW_DAYS and use Resync all to include them.
        </div>
      )}
      {/* `seedState.skippedNoteIds` is deliberately NOT rendered. Those are
          notes Granola's own API returns 5xx for; there is no key to re-paste,
          no setting to change, and no button that helps — Brain already
          re-attempts each one on every sync tick and they land by themselves if
          Granola ever repairs them. A warning a reader cannot act on just
          teaches them to distrust the panel. It stays observable where someone
          CAN act on it: the `granola:seed`/`granola:sync` warn logs and the
          persisted seed_state. Contrast droppedByWindow above, which is shown
          precisely because it names the config change that fixes it. */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account card
// ---------------------------------------------------------------------------

function AccountCard({ account }: { account: GranolaAccount }) {
  const status = STATUS[account.status];

  return (
    <div className="rounded-xl border border-border p-6">
      <div className="flex items-center gap-4">
        <IconTile tone="green">
          <NotebookPen className="h-5 w-5" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-[15px]">{account.label || 'Granola account'}</p>
          <p className="truncate font-mono text-[13px] text-faint">
            grn_…{account.keyLast4} · Connected {new Date(account.createdAt).toLocaleDateString()} ·
            Last synced {account.lastSyncedAt ? formatRelative(account.lastSyncedAt) : 'never'}
          </p>
        </div>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        <Link
          to="/sources/$connector/$accountId"
          params={{ connector: 'granola', accountId: account.id }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
        >
          View
        </Link>
      </div>

      {(account.status === 'invalid_key' || account.status === 'error') && account.lastError && (
        <p className="mt-3 text-[13px] text-destructive">{account.lastError}</p>
      )}

      <SeedProgress seedState={account.seedState} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section — header, Resync all, account cards.
// ---------------------------------------------------------------------------

export function GranolaConnectedAccounts() {
  const qc = useQueryClient();

  const { data: accounts } = useQuery({
    queryKey: qk.granolaAccounts,
    queryFn: () => granolaAccountApi.list(),
    // Granola may be unconfigured (503) — stay quiet rather than retrying.
    retry: false,
  });

  const resyncAll = useMutation({
    mutationFn: () => granolaAccountApi.resyncAll(),
    onSuccess: (res) => {
      toast.success(
        res.resynced.length > 0
          ? `Resyncing ${res.resynced.length} Granola account${res.resynced.length === 1 ? '' : 's'}…`
          : 'Nothing to resync.',
      );
      void qc.invalidateQueries({ queryKey: qk.granolaAccounts });
    },
    onError: () => toast.error('Failed to start resync'),
  });

  const live = (accounts ?? []).filter((a) => a.status !== 'disconnected');
  if (live.length === 0) return null;

  return (
    <div className="mt-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-semibold text-base">Connected Granola accounts</h2>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Meeting notes and transcripts from each connected account. Everyone in this Brain
            workspace can search them.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={resyncAll.isPending}
          onClick={() => resyncAll.mutate()}
        >
          {resyncAll.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Resync all
        </Button>
      </div>

      <div className="mt-4 flex flex-col gap-4">
        {live.map((a) => (
          <AccountCard key={a.id} account={a} />
        ))}
      </div>
    </div>
  );
}
